import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { NodonTheme } from '@client/style';
import { CommentRowBase } from './CommentRowBase';
import { DotLoader } from '@ui/DotLoader';
import { User } from '@shared/models/user.model';
import { IComment } from '@shared/models/comment.model';
import { DateTime, Duration } from 'luxon';
import { useObjectMemoDeep } from '@client/hooks/use-object.hooks';

type CommentLoaderProps = {
  user: User;
  comment: IComment;
};

// just a helper to start a carousel of texts that will swap every swapIntervalMs and stop at the last text
const useLoadingText = (
  texts: NonEmptyArray<string>,
  startTime: Date,
  swapIntervalMs: number,
) => {
  const memoTexts = useObjectMemoDeep(texts);
  const [loadingText, setLoadingText] = useState<string>('');
  useEffect(() => {
    const lastText = memoTexts.at(-1) as string; // last text is guaranteed to be in the array since it's non-empty

    const getCurrentText = () => {
      const timeSinceStart = DateTime.now()
        .diff(DateTime.fromJSDate(startTime))
        .as('milliseconds');
      const currentTextIndex = Math.floor(timeSinceStart / swapIntervalMs);
      return memoTexts[currentTextIndex] ?? lastText;
    };

    const firstText = getCurrentText();
    setLoadingText(firstText);
    if (firstText === lastText) return; // no need to start the interval

    const interval = setInterval(() => {
      const nextText = getCurrentText();
      setLoadingText(nextText);
      if (nextText === lastText) clearTimeout(interval); // funky to clear myself!
    }, 500);

    return () => clearTimeout(interval);
  }, [memoTexts, swapIntervalMs, startTime]);
  return loadingText;
};

/**
 * Loading row used when waiting for a response from the AI.
 * Tries to imitate a regular row but with a dot loader instead of a message.
 * @returns
 */
export const CommentLoader: React.FC<CommentLoaderProps> = ({
  user,
  comment,
}) => {
  const startTime = useMemo(
    () => new Date(comment.created_at),
    [comment.created_at],
  );
  const loadingText = useLoadingText(
    ['Thinking', 'Still thinking', 'Formulating answer'],
    startTime,
    Duration.fromObject({ seconds: 10 }).as('milliseconds'),
  );

  return (
    <CommentRowBase
      user={user}
      rightContent={
        <Box>
          <Typography variant="body2" color={NodonTheme.palette.text.secondary}>
            {loadingText}
            <DotLoader />
          </Typography>
        </Box>
      }
    />
  );
};
